import { formatDateTimeForLocale } from '@/lib/org-date-locale'

export const DEFAULT_PLANNED_DURATION = 'hours:8'
const WORK_HOURS_PER_DAY = 8

export const DEFAULT_PLANNED_START_TIME = '09:00'

const PLANNED_START_TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hours = Math.floor(index / 2)
  const minutes = index % 2 === 0 ? '00' : '30'
  const value = `${String(hours).padStart(2, '0')}:${minutes}`
  const label = new Date(`1970-01-01T${value}:00`).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  return { value, label }
})

export const PLANNED_DURATION_OPTIONS = [
  {
    value: 'minutes:30',
    label: '30 minutes',
    group: 'minutes' as const,
  },
  ...Array.from({ length: 12 }, (_, index) => {
    const amount = index + 1
    return {
      value: `hours:${amount}`,
      label: `${amount} hour${amount === 1 ? '' : 's'}`,
      group: 'hours' as const,
    }
  }),
]

export const extractTimeFromDateTimeLocal = (value?: string | null) => {
  if (!value?.trim()) {
    return ''
  }

  const match = value.match(/T(\d{2}:\d{2})/)
  return match?.[1] ?? ''
}

export const snapTimeToInterval = (time?: string | null, fallback = DEFAULT_PLANNED_START_TIME) => {
  if (!time?.trim()) {
    return fallback
  }

  const [hoursRaw, minutesRaw] = time.split(':')
  const hours = Number(hoursRaw)
  const minutes = Number(minutesRaw)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return fallback
  }

  const totalMinutes = hours * 60 + minutes
  const snapped = Math.round(totalMinutes / 30) * 30
  const snappedHours = Math.floor(snapped / 60) % 24
  const snappedMinutes = snapped % 60

  return `${String(snappedHours).padStart(2, '0')}:${String(snappedMinutes).padStart(2, '0')}`
}

export const buildPlannedStartDateTime = (workDate?: string, time = DEFAULT_PLANNED_START_TIME) => {
  if (!workDate?.trim()) {
    return ''
  }

  return `${workDate}T${time}`
}

export const extractDateFromDateTimeLocal = (value?: string | null) => {
  if (!value?.trim()) {
    return ''
  }

  return value.slice(0, 10)
}

export const buildPlannedStartOptions = (workDate?: string) => {
  if (!workDate?.trim()) {
    return []
  }

  return PLANNED_START_TIME_OPTIONS.map((timeOption) => ({
    value: buildPlannedStartDateTime(workDate, timeOption.value),
    label: timeOption.label,
  }))
}

const parsePlannedDuration = (value: string) => {
  const [unit, amountRaw] = value.split(':')
  const amount = Number(amountRaw)
  if ((unit !== 'minutes' && unit !== 'hours' && unit !== 'days') || !Number.isFinite(amount) || amount <= 0) {
    return null
  }

  return { unit, amount }
}

const addPlannedDuration = (start: Date, durationValue: string) => {
  const parsed = parsePlannedDuration(durationValue)
  if (!parsed) {
    return null
  }

  const end = new Date(start.getTime())
  if (parsed.unit === 'minutes') {
    end.setTime(end.getTime() + parsed.amount * 60 * 1000)
  } else if (parsed.unit === 'hours') {
    end.setTime(end.getTime() + parsed.amount * 60 * 60 * 1000)
  } else {
    end.setDate(end.getDate() + parsed.amount)
  }

  return end
}

export const durationToHours = (durationValue: string) => {
  const parsed = parsePlannedDuration(durationValue)
  if (!parsed) {
    return '0.00'
  }

  if (parsed.unit === 'minutes') {
    return (parsed.amount / 60).toFixed(2)
  }

  if (parsed.unit === 'hours') {
    return parsed.amount.toFixed(2)
  }

  return (parsed.amount * WORK_HOURS_PER_DAY).toFixed(2)
}

export const inferPlannedDuration = (start?: string, end?: string) => {
  if (!start?.trim() || !end?.trim()) {
    return DEFAULT_PLANNED_DURATION
  }

  const startDate = new Date(start)
  const endDate = new Date(end)
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate <= startDate) {
    return DEFAULT_PLANNED_DURATION
  }

  const diffMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (60 * 1000))
  if (diffMinutes === 30) {
    return 'minutes:30'
  }

  const diffHours = Math.min(12, Math.max(1, Math.round(diffMinutes / 60)))
  if (diffMinutes >= 60) {
    return `hours:${diffHours}`
  }

  return DEFAULT_PLANNED_DURATION
}

const getTimeZoneOffsetMs = (date: Date, timeZone?: string) => {
  if (!timeZone) {
    return 0
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)

  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0)

  const asUtc = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    value('hour'),
    value('minute'),
    value('second'),
  )

  return asUtc - date.getTime()
}

export const orgWallClockToApiDateTime = (value?: string, timeZone?: string) => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) {
    return undefined
  }

  if (!timeZone) {
    const parsed = new Date(trimmed)
    return Number.isNaN(parsed.getTime()) ? trimmed : parsed.toISOString()
  }

  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
  if (!match) {
    const parsed = new Date(trimmed)
    return Number.isNaN(parsed.getTime()) ? trimmed : parsed.toISOString()
  }

  const [, year, month, day, hour, minute] = match
  const localAsUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  )
  let utcMillis = localAsUtc

  for (let index = 0; index < 2; index += 1) {
    utcMillis = localAsUtc - getTimeZoneOffsetMs(new Date(utcMillis), timeZone)
  }

  return new Date(utcMillis).toISOString()
}

const toDateTimeLocalValue = (value?: string | null) => {
  if (!value) {
    return ''
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return ''
  }

  const offset = parsed.getTimezoneOffset() * 60_000
  return new Date(parsed.getTime() - offset).toISOString().slice(0, 16)
}

export const toLocalWallClockInputValue = (value?: string | null) => {
  const text = String(value ?? '').trim()
  if (!text) {
    return ''
  }

  if (/(?:Z|[+-]\d{2}:?\d{2})$/u.test(text)) {
    return ''
  }

  const match = text.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/)
  if (match) {
    return `${match[1]}T${match[2]}`
  }

  return ''
}

export const toEntryDateTimeLocalValue = (
  utcValue?: string | null,
  localValue?: string | null,
  timeZone?: string,
) => {
  const localInputValue = toLocalWallClockInputValue(localValue)
  if (localInputValue) {
    return localInputValue
  }

  const trimmedUtc = utcValue?.trim()
  if (trimmedUtc) {
    const parsed = new Date(trimmedUtc)
    if (!Number.isNaN(parsed.getTime())) {
      return toOrgWallClockDateTimeLocal(parsed, timeZone)
    }
  }

  return ''
}

export const resolveAttendanceCheckInWallClock = ({
  checkInUtc,
  checkInLocal,
  timeZone,
  fallback = '',
}: {
  checkInUtc?: string | null
  checkInLocal?: string | null
  timeZone?: string
  fallback?: string
}) => {
  const fromEntry = toEntryDateTimeLocalValue(checkInUtc, checkInLocal, timeZone)
  if (fromEntry.trim()) {
    return fromEntry
  }

  return fallback.trim()
}

export const resolvePlannedEndFromStart = (plannedStart?: string, durationValue = DEFAULT_PLANNED_DURATION) => {
  if (!plannedStart?.trim()) {
    return ''
  }

  const startDate = new Date(plannedStart)
  if (Number.isNaN(startDate.getTime())) {
    return ''
  }

  const endDate = addPlannedDuration(startDate, durationValue)
  if (!endDate) {
    return ''
  }

  return toDateTimeLocalValue(endDate.toISOString())
}

export const toOptionalNumberString = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  return String(value)
}

export const loggedDurationToTotalMinutes = (hours: string, minutes: string) => {
  const parsedHours = Math.max(0, Number(hours) || 0)
  const parsedMinutes = Math.max(0, Math.min(59, Number(minutes) || 0))
  return parsedHours * 60 + parsedMinutes
}

export const totalMinutesToLoggedDuration = (totalMinutes: number) => {
  const safe = Math.max(0, Math.round(totalMinutes))
  return {
    hours: String(Math.floor(safe / 60)),
    minutes: String(safe % 60),
  }
}

export const toOrgWallClockDateTimeLocal = (date = new Date(), timeZone?: string) => {
  if (!timeZone) {
    return toDateTimeLocalValue(date.toISOString())
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)

  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '00'

  return `${value('year')}-${value('month')}-${value('day')}T${value('hour')}:${value('minute')}`
}

export const wallClockToInstantMs = (value?: string, timeZone?: string) => {
  const trimmed = value?.trim()
  if (!trimmed) {
    return Number.NaN
  }

  const iso = orgWallClockToApiDateTime(trimmed, timeZone)
  if (!iso) {
    return Number.NaN
  }

  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime()) ? Number.NaN : parsed.getTime()
}

export const computeHoursFromDateTimeRange = (
  checkIn?: string,
  checkOut?: string,
  timeZone?: string,
) => {
  if (!checkIn?.trim() || !checkOut?.trim()) {
    return '0.00'
  }

  const startMs = wallClockToInstantMs(checkIn, timeZone)
  const endMs = wallClockToInstantMs(checkOut, timeZone)
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) {
    return '0.00'
  }

  return ((endMs - startMs) / 3_600_000).toFixed(2)
}

export const validateAttendanceInterval = (
  checkIn?: string,
  checkOut?: string,
  timeZone?: string,
) => {
  const checkInValue = checkIn?.trim()
  const checkOutValue = checkOut?.trim()

  if (!checkInValue) {
    return 'Check-in is required.'
  }

  if (!checkOutValue) {
    return null
  }

  const startMs = wallClockToInstantMs(checkInValue, timeZone)
  const endMs = wallClockToInstantMs(checkOutValue, timeZone)
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return 'Enter valid check-in and check-out times.'
  }

  if (startMs > endMs) {
    return 'Check-in must be on or before check-out.'
  }

  return null
}

export const validateCheckoutAgainstExistingCheckIn = ({
  checkInWallClock,
  checkInUtc,
  checkOutWallClock,
  timeZone,
  assumeExistingCheckIn = false,
}: {
  checkInWallClock?: string
  checkInUtc?: string | null
  checkOutWallClock?: string
  timeZone?: string
  assumeExistingCheckIn?: boolean
}) => {
  const checkOutValue = checkOutWallClock?.trim()
  if (!checkOutValue) {
    return null
  }

  const checkOutIso = orgWallClockToApiDateTime(checkOutValue, timeZone)
  const checkOutInstant = checkOutIso ? new Date(checkOutIso) : null
  if (!checkOutInstant || Number.isNaN(checkOutInstant.getTime())) {
    return 'Enter a valid check-out time.'
  }

  const resolvedCheckInWallClock = checkInWallClock?.trim()
    || toEntryDateTimeLocalValue(checkInUtc, null, timeZone)

  if (resolvedCheckInWallClock) {
    return validateAttendanceInterval(resolvedCheckInWallClock, checkOutValue, timeZone)
  }

  const checkInInstant = checkInUtc?.trim() ? new Date(checkInUtc) : null
  if (checkInInstant && !Number.isNaN(checkInInstant.getTime())) {
    if (checkOutInstant <= checkInInstant) {
      return 'Check-in must be on or before check-out.'
    }
    return null
  }

  if (assumeExistingCheckIn) {
    return null
  }

  return 'Check-in is required.'
}

export const computeAttendanceFromLogged = ({
  actualStart,
  loggedHours,
  loggedMinutes,
  timeZone,
}: {
  actualStart?: string
  loggedHours: string
  loggedMinutes: string
  timeZone?: string
}) => {
  const totalMinutes = loggedDurationToTotalMinutes(loggedHours, loggedMinutes)
  const startValue = actualStart?.trim() || ''
  if (!startValue) {
    return { checkIn: '', checkOut: '', hours: '0.00' }
  }

  const startMs = wallClockToInstantMs(startValue, timeZone)
  if (!Number.isFinite(startMs)) {
    return { checkIn: '', checkOut: '', hours: '0.00' }
  }

  if (totalMinutes <= 0) {
    return {
      checkIn: startValue,
      checkOut: '',
      hours: '0.00',
    }
  }

  const endMs = startMs + totalMinutes * 60 * 1000
  return {
    checkIn: startValue,
    checkOut: toOrgWallClockDateTimeLocal(new Date(endMs), timeZone),
    hours: (totalMinutes / 60).toFixed(2),
  }
}

export const formatAttendancePreview = (value?: string, locale = 'en-US', timeZone?: string) => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) {
    return '—'
  }

  const hasExplicitTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/u.test(trimmed)
  const wallClockMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)

  if (wallClockMatch && !hasExplicitTimeZone) {
    const instantMs = wallClockToInstantMs(trimmed, timeZone)
    if (Number.isFinite(instantMs)) {
      return formatDateTimeForLocale(new Date(instantMs), locale, '—', timeZone)
    }
  }

  const parsed = new Date(trimmed)
  if (!Number.isNaN(parsed.getTime())) {
    return formatDateTimeForLocale(parsed, locale, '—', timeZone)
  }

  return trimmed
}

export const resolveLoggedAttendancePreviewCheckOut = ({
  computedCheckOut,
  savedCheckOut,
  checkOutUtc,
  checkOutLocal,
  timeZone,
}: {
  computedCheckOut?: string | null
  savedCheckOut?: string | null
  checkOutUtc?: string | null
  checkOutLocal?: string | null
  timeZone?: string
}) => {
  const computed = String(computedCheckOut ?? '').trim()
  if (computed) {
    return computed
  }

  const saved = String(savedCheckOut ?? '').trim()
  if (saved) {
    return saved
  }

  return toEntryDateTimeLocalValue(checkOutUtc, checkOutLocal, timeZone)
}

export const buildReviewPayloadFromEntry = (entry: {
  peak_time_approved?: boolean
  holiday_approved?: boolean
  special_day_approved?: boolean
  over_time_approved?: boolean
  night_pay_approved?: boolean
  rating?: string | number | null
  timely_arrived_rating?: string | number | null
  quality_work_rating?: string | number | null
  conduct_rating?: string | number | null
  review_comment?: string | null
}) => {
  const toOptionalRatingPayload = (value?: string | number | null) => {
    const trimmed = String(value ?? '').trim()
    return trimmed ? trimmed : null
  }

  return {
    peak_time_approved: Boolean(entry.peak_time_approved),
    holiday_approved: Boolean(entry.holiday_approved),
    special_day_approved: Boolean(entry.special_day_approved),
    over_time_approved: Boolean(entry.over_time_approved),
    night_pay_approved: Boolean(entry.night_pay_approved),
    rating: toOptionalRatingPayload(entry.rating),
    timely_arrived_rating: toOptionalRatingPayload(entry.timely_arrived_rating),
    quality_work_rating: toOptionalRatingPayload(entry.quality_work_rating),
    conduct_rating: toOptionalRatingPayload(entry.conduct_rating),
    review_comment: entry.review_comment ?? '',
  }
}
